import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  Modal,
  ScrollArea,
  Grid,
  Button,
  Stack,
  Alert,
  Card,
  Group,
  Text,
  Divider,
} from "@mantine/core";
import {
  IconPackage,
  IconShoppingBag,
  IconGiftCard,
} from "@tabler/icons-react";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { useSalon } from "../../../../../context/SalonContext";
import { useClient } from "../../../../../context/ClientContext";
import { useApp } from "../../../../../context/AppContext";
import { useTransaction } from "../../../../../context/TransactionContext";
import { BusinessInfo } from "../../../../../types/context";
import {
  EditTransactionLineItemsRequest,
  GetTransactionsResponse,
  TransactionResponse,
  Payment,
} from "../../../../../types/transactions";
import {
  QuickSaleProductState,
  CreateQuickSaleRequest,
} from "../../../../../types/quickSale";
import { ServicePackage } from "../../../../../types/packages";
import { SellGiftCardResponse } from "../../../../../types/giftCards";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { computeTransactionTotals } from "../../../../../utils/transactionTotals";
import { calculateGroupedDiscounts } from "../../../../../utils/transactionUtils";
import { formatDiscountValue } from "../../../../../utils/discountFormatting";
import {
  buildDiscountableLines,
  buildLineDiscountsRequest,
  buildRemoveDiscountGroupRequest,
  DiscountGroup,
  getLineDiscountEntries,
  LineDiscountChange,
} from "../../../../../utils/lineItemDiscounts";
import {
  buildCartProductEdits,
  buildQuickSaleProductPayload,
  calculateCartDiscountTotal,
  syncCartDiscountsFromTransaction,
} from "../../../../../utils/quickSaleCart";
import ClientLookup from "../../../../common/ClientLookup";
import QuickSaleDetails from "./QuickSaleDetails";
import QuickSaleSummary from "./QuickSaleSummary";
import PackageSpecialPicker from "../PackageSpecialPicker";
import ApplyDiscountModal from "../TransactionDetailModal/ApplyDiscountModal";
import CheckoutScreenModal from "../../../../checkout/CheckoutPanel/CheckoutScreenModal";
import GiftCardSaleModal from "./GiftCardSaleModal";
import GiftCardPostCheckoutModal from "./GiftCardPostCheckoutModal";
import GiftCardLineItemForm from "../TransactionDetailModal/GiftCardLineItemForm";
import useCanSellGiftCards from "../../../../../hooks/useCanSellGiftCards";
import sharedStyles from "../SharedModalLayout.module.css";

interface QuickSaleModalProps {
  opened: boolean;
  onClose: () => void;
  onQuickSaleComplete?: (transactionId: string) => void;
  existingTransaction?: GetTransactionsResponse | null; // For viewing/editing existing Quick Sales
}

type ModalStep = "client" | "saleType" | "products";

export default function QuickSaleModal({
  opened,
  onClose,
  onQuickSaleComplete,
  existingTransaction,
}: QuickSaleModalProps) {
  const { isMobile, isTablet } = useResponsive();
  const { businessInfo, employees, currentUser } = useSalon() as {
    businessInfo: BusinessInfo | null;
    employees: any[];
    currentUser: any;
  };
  const { allClients } = useClient();
  const {
    createQuickSale,
    getTransaction,
    addLineItemToTransaction,
    addGiftCardLineItem,
    getServicePackages,
    sellServicePackage,
    editTransactionLineItems,
    voidTransaction,
  } = useApp();
  const { deleteLineItem } = useTransaction();
  // Gift cards ship dark behind an env flag AND must be enabled for this
  // location in Settings → Gift Cards (businessInfo.gift_cards_enabled, from
  // gift_card_configurations.is_enabled). The sell action is hidden entirely as
  // a Quick Sale option unless both are on; while businessInfo is loading it is
  // nullish, so the option stays hidden (safe default).
  const giftCardsEnabled =
    process.env.REACT_APP_GIFT_CARDS_ENABLED === "true" &&
    businessInfo?.gift_cards_enabled === true;
  // Role permission (giftCards.canSellGiftCards): hides only the SELL entry
  // points. Gift-card lines already on a sale still render and stay removable.
  const canSellGiftCards = useCanSellGiftCards();
  // Modal step state - start at products if we have an existing transaction
  const [step, setStep] = useState<ModalStep>(
    existingTransaction ? "products" : "client",
  );

  // Client state
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [clientError, setClientError] = useState<string>("");

  // Products state (in-memory)
  const [products, setProducts] = useState<QuickSaleProductState[]>([]);

  // Transaction state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdTransaction, setCreatedTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // ── Package-sale sub-flow ──────────────────────────────────────
  // Selling a package is a separate 100%-upfront flow from the products cart:
  // pick a package for the already-selected client, call sellServicePackage,
  // then reuse the checkout screen for the sale (mirrors SellPackageModal).
  const [showPackageSell, setShowPackageSell] = useState(false);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  );
  const [isSellingPackage, setIsSellingPackage] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageTransaction, setPackageTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [packageRemainingBalance, setPackageRemainingBalance] = useState(0);
  // Sales tax included in the package sale amount (0 when the package
  // omits tax collection) — drives the "Includes $X sales tax" note.
  const [packageTaxTotal, setPackageTaxTotal] = useState(0);
  // Review step between selling the package and checkout — the only place a
  // discount can be applied (the package line exists only after the sale).
  const [showPackageReview, setShowPackageReview] = useState(false);
  const [isPackageDiscountModalOpen, setIsPackageDiscountModalOpen] =
    useState(false);
  // Guards the review-step discount actions (Add / Change / Remove) against a
  // concurrent double-fire of editTransactionLineItems.
  const [isApplyingPackageDiscount, setIsApplyingPackageDiscount] =
    useState(false);
  // The package-line discount group being changed, if any.
  const [packageDiscountGroup, setPackageDiscountGroup] =
    useState<DiscountGroup | null>(null);
  const [showPackageCheckout, setShowPackageCheckout] = useState(false);

  // ── Gift-card-sale sub-flow ────────────────────────────────────
  // Selling a gift card mirrors the package sub-flow: a dedicated modal creates
  // its own unpaid sale transaction (sellGiftCard), then the checkout screen is
  // reused for payment. The card is minted server-side once the sale is paid.
  const [showGiftCardSell, setShowGiftCardSell] = useState(false);
  // Add-a-gift-card-as-a-line-item (to the current multi-item quick sale, once a
  // real transaction exists). Distinct from the standalone GiftCardSaleModal.
  const [showGiftCardLineItemModal, setShowGiftCardLineItemModal] =
    useState(false);
  const [giftCardTransaction, setGiftCardTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [giftCardRemainingBalance, setGiftCardRemainingBalance] = useState(0);
  const [giftCardTaxTotal, setGiftCardTaxTotal] = useState(0);
  const [showGiftCardCheckout, setShowGiftCardCheckout] = useState(false);
  // The sale transaction's UUID, kept to load the minted card post-checkout.
  const [giftCardTransactionUuid, setGiftCardTransactionUuid] = useState("");
  // True once the gift-card sale is fully paid — gates the post-checkout fork.
  // Kept as a ref (not state) because the checkout's close handler must read the
  // CURRENT value: the non-card tender calls onTransactionUpdated and then
  // schedules the close inside a 3s setTimeout in the SAME tick, freezing the
  // render-time close closure. A captured state value would still be the
  // pre-payment `false`, wrongly taking the "abandoned → close everything"
  // branch; a ref is mutated in place and read at call time, so the frozen
  // closure sees the paid value. (Same stale-closure guard as
  // `createdTransactionRef`.)
  const giftCardPaidRef = useRef(false);
  const [showGiftCardPostCheckout, setShowGiftCardPostCheckout] =
    useState(false);

  // Ref-counter for in-flight tax recomputes (create / add / update / remove
  // + subsequent refetch). Using a ref-counter instead of a plain boolean so
  // overlapping calls — e.g. rapid keystrokes in handleUpdateProduct — can't
  // have the first-finishing call clear the flag while a later refetch is
  // still running. The boolean state below is driven from the counter purely
  // so React re-renders pick it up.
  const [isTaxesLoading, setIsTaxesLoading] = useState(false);
  const taxesLoadingCountRef = useRef(0);
  const beginTaxesLoading = useCallback(() => {
    taxesLoadingCountRef.current += 1;
    setIsTaxesLoading(true);
  }, []);
  const endTaxesLoading = useCallback(() => {
    taxesLoadingCountRef.current = Math.max(
      0,
      taxesLoadingCountRef.current - 1,
    );
    if (taxesLoadingCountRef.current === 0) {
      setIsTaxesLoading(false);
    }
  }, []);

  // Ref to track latest transaction value (avoids stale closure in callbacks)
  const createdTransactionRef = useRef<GetTransactionsResponse | null>(null);
  // Guard against concurrent createQuickSale calls when rapidly adding products
  const isCreatingTransactionRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    createdTransactionRef.current = createdTransaction;
  }, [createdTransaction]);

  // Load existing transaction if provided
  useEffect(() => {
    if (existingTransaction && opened) {
      // Set the client
      setSelectedClientId(existingTransaction.client.id);

      // Load products from transaction
      const loadedProducts: QuickSaleProductState[] =
        existingTransaction.line_items.products.map((product) => ({
          productId: product.id,
          productName: product.name,
          quantity: product.quantity,
          unitPrice: product.unit_price,
          imgUrl: product.img_url,
          availableStock: product.available_stock,
          discounts: getLineDiscountEntries(product),
          staffCommissionTarget:
            product.staff_commission_target !== undefined
              ? product.staff_commission_target
              : undefined,
        }));

      setProducts(loadedProducts);
      setCreatedTransaction(existingTransaction);

      // Check if transaction is fully paid (view-only mode)
      const txnStatus = (existingTransaction.transaction as any)
        .transaction_status;
      const isFullyPaid = txnStatus === "paid";
      setIsViewOnly(isFullyPaid);

      setStep("products");
    }
  }, [existingTransaction, opened]);

  // Auto-void on exit: closing a NEW quick sale (not one resumed via
  // `existingTransaction`) whose cart transaction exists but has nothing paid
  // voids it as `auto`, so the abandoned sale drops out of totals while staying
  // checkout-able from Appointment Details (paying it un-voids it
  // server-side). Covers an abandoned checkout too. Fire-and-forget: closing is
  // never blocked, and a rejection (e.g. 409 for an unrefunded card payment)
  // just leaves the sale open. Reads the ref so a frozen close closure still
  // sees the latest transaction. Package / standalone gift-card sub-flow sales
  // are not touched.
  const autoVoidAbandonedSale = useCallback(() => {
    if (existingTransaction) return;
    const txn = createdTransactionRef.current;
    const businessLocationId = businessInfo?.id;
    const transactionUuid = txn?.transaction?.id;
    if (!txn || !transactionUuid || !businessLocationId) return;
    if (txn.transaction.transaction_status !== "unpaid") return;
    if ((txn.payments ?? []).some((p) => p.status === "succeeded")) return;
    Promise.resolve()
      .then(() =>
        voidTransaction(businessLocationId, transactionUuid, { auto: true }),
      )
      .catch((err) => {
        console.warn("Quick sale auto-void skipped:", err);
      });
  }, [existingTransaction, businessInfo?.id, voidTransaction]);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    autoVoidAbandonedSale();
    setStep("client");
    setSelectedClientId("");
    setClientError("");
    setProducts([]);
    setIsLoading(false);
    setError(null);
    setCreatedTransaction(null);
    setShowCheckoutModal(false);
    setIsViewOnly(false);
    setShowPackageSell(false);
    setSelectedPackageId(null);
    setIsSellingPackage(false);
    setPackageError(null);
    setPackageTransaction(null);
    setPackageRemainingBalance(0);
    setPackageTaxTotal(0);
    setShowPackageReview(false);
    setIsPackageDiscountModalOpen(false);
    setPackageDiscountGroup(null);
    setIsApplyingPackageDiscount(false);
    setShowPackageCheckout(false);
    setShowGiftCardSell(false);
    setGiftCardTransaction(null);
    setGiftCardRemainingBalance(0);
    setGiftCardTaxTotal(0);
    setShowGiftCardCheckout(false);
    setGiftCardTransactionUuid("");
    giftCardPaidRef.current = false;
    setShowGiftCardPostCheckout(false);
    onClose();
  }, [onClose, autoVoidAbandonedSale]);

  // Handle client selection
  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    setClientError("");
  };

  // Proceed after client selection to the sale-type chooser (products vs.
  // package/special).
  const handleProceedFromClient = () => {
    if (!selectedClientId) {
      setClientError("Please select a client");
      return;
    }
    setStep("saleType");
  };

  // Load the salon's packages the first time the package-sell picker opens.
  const salonId = businessInfo?.id ?? "";
  useEffect(() => {
    if (!showPackageSell || !salonId) return;
    let cancelled = false;
    setIsLoadingPackages(true);
    (async () => {
      try {
        const list = await getServicePackages(salonId);
        if (!cancelled) setPackages(list);
      } catch (err) {
        console.error("Failed to load service packages:", err);
        if (!cancelled) setPackageError("Failed to load packages.");
      } finally {
        if (!cancelled) setIsLoadingPackages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPackageSell, salonId, getServicePackages]);

  const handleOpenPackageSell = () => {
    if (!selectedClientId) {
      setClientError("Please select a client");
      return;
    }
    setSelectedPackageId(null);
    setPackageError(null);
    setShowPackageSell(true);
  };

  const handleSellPackage = async () => {
    if (!selectedPackageId) {
      setPackageError("Please select a package.");
      return;
    }
    // A package sale is committed server-side the moment it's created, so never
    // sell twice for one Quick Sale — jump straight to the review of the
    // existing sale instead of orphaning it with a duplicate.
    if (packageTransaction) {
      setShowPackageSell(false);
      setShowPackageReview(true);
      return;
    }
    setIsSellingPackage(true);
    setPackageError(null);
    try {
      const result = await sellServicePackage(salonId, {
        client_id: selectedClientId,
        service_package_id: selectedPackageId,
      });
      // getTransaction looks up by the transaction UUID (bookingId query param),
      // NOT the numeric transaction_id — passing the int makes the backend throw
      // "invalid input syntax for type uuid" and the sale fails to load.
      const fullTransaction = await getTransaction(result.transaction_uuid);
      if (!fullTransaction) {
        setPackageError(
          "Sale created but the transaction could not be loaded.",
        );
        return;
      }
      setPackageTransaction(fullTransaction);
      // `amount` is TAX-INCLUSIVE — it is what checkout charges.
      setPackageRemainingBalance(Number(result.amount.toFixed(2)));
      setPackageTaxTotal(Number((result.tax_total ?? 0).toFixed(2)));
      setShowPackageSell(false);
      // Land on the review step so an optional discount can be applied before
      // payment (the package line now exists on a real transaction).
      setShowPackageReview(true);
    } catch (err) {
      console.error("Failed to sell service package:", err);
      setPackageError(
        err instanceof Error ? err.message : "Failed to sell package.",
      );
    } finally {
      setIsSellingPackage(false);
    }
  };

  const handlePackageTransactionUpdated = (
    updated: GetTransactionsResponse | TransactionResponse,
  ) => {
    setPackageTransaction(updated as GetTransactionsResponse);
    const paid =
      (updated.payments || [])
        .filter((p) => p.status === "succeeded")
        .reduce((sum, p) => sum + p.amount, 0) || 0;
    const due = packageRemainingBalance - paid;
    if (due <= 0.01 && onQuickSaleComplete) {
      onQuickSaleComplete(updated.transaction.id);
    }
  };

  // Open the gift-card sale modal. Like packages, the purchaser is the
  // already-selected Quick Sale client (required to reach the sale-type step).
  const handleOpenGiftCardSell = () => {
    if (!selectedClientId) {
      setClientError("Please select a client");
      return;
    }
    setShowGiftCardSell(true);
  };

  // The sale modal created an unpaid gift-card transaction; load it and hand it
  // to the checkout screen (mirrors handleSellPackage's post-sell wiring). Uses
  // the transaction UUID for the load — the numeric id makes the backend throw.
  const handleGiftCardSold = async (result: SellGiftCardResponse) => {
    try {
      const fullTransaction = await getTransaction(result.transaction_uuid);
      if (!fullTransaction) {
        setError(
          "Gift card sale created but the transaction could not be loaded.",
        );
        return;
      }
      setGiftCardTransaction(fullTransaction);
      setGiftCardTransactionUuid(result.transaction_uuid);
      giftCardPaidRef.current = false;
      // `amount` is the checkout charge (tax-exempt, so it equals the face value).
      setGiftCardRemainingBalance(Number(result.amount.toFixed(2)));
      setGiftCardTaxTotal(Number((result.tax_total ?? 0).toFixed(2)));
      setShowGiftCardSell(false);
      setShowGiftCardCheckout(true);
    } catch (err) {
      console.error("Failed to load gift card sale transaction:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Gift card sale created but the transaction could not be loaded.",
      );
    }
  };

  const handleGiftCardTransactionUpdated = (
    updated: GetTransactionsResponse | TransactionResponse,
  ) => {
    setGiftCardTransaction(updated as GetTransactionsResponse);
    const paid =
      (updated.payments || [])
        .filter((p) => p.status === "succeeded")
        .reduce((sum, p) => sum + p.amount, 0) || 0;
    const due = giftCardRemainingBalance - paid;
    // Fully paid: mark it (so dismissing the checkout leads to the post-checkout
    // fork) and refresh the parent's transaction list — once. Set the ref
    // synchronously here so the checkout's close handler (which can fire from a
    // frozen setTimeout closure on the non-card tender) reads the paid value.
    if (due <= 0.01 && !giftCardPaidRef.current) {
      giftCardPaidRef.current = true;
      if (onQuickSaleComplete) {
        onQuickSaleComplete(updated.transaction.id);
      }
    }
  };

  // Dismissing the gift-card checkout: if it was paid, advance to the
  // post-checkout fork (attach the minted card / text the recipient a link);
  // otherwise the sale was abandoned, so just close.
  const handleGiftCardCheckoutClose = () => {
    setShowGiftCardCheckout(false);
    // Read the ref, not the state: the non-card tender schedules this close in a
    // 3s setTimeout that captured the render-time (pre-payment) `giftCardPaid`.
    if (giftCardPaidRef.current) {
      setShowGiftCardPostCheckout(true);
    } else {
      handleClose();
    }
  };

  const handleGiftCardPostCheckoutDone = () => {
    setShowGiftCardPostCheckout(false);
    handleClose();
  };

  // Add a gift card as a LINE ITEM on the current quick sale. Only reachable once
  // a real transaction exists (a gift card has no product_uuid, so it can't be the
  // first item that lazily creates it — the standalone GiftCardSaleModal covers a
  // gift-card-only sale). Posts via addGiftCardLineItem, then refetches so the
  // summary + balance reflect it.
  const handleAddGiftCardLineItem = async (payload: {
    gift_cards: Array<{ amount: number; code?: string }>;
  }) => {
    const current = createdTransactionRef.current;
    if (!current) return;
    setShowGiftCardLineItemModal(false);
    beginTaxesLoading();
    try {
      await addGiftCardLineItem(current.transaction.id, payload.gift_cards[0]);
      await refetchCreatedTransaction();
    } catch (err) {
      console.error("Failed to add gift card line item:", err);
      setError(err instanceof Error ? err.message : "Failed to add gift card");
    } finally {
      endTaxesLoading();
    }
  };

  const handleRemoveGiftCardLineItem = async (lineItemId: string) => {
    beginTaxesLoading();
    try {
      await deleteLineItem(lineItemId);
      await refetchCreatedTransaction();
    } catch (err) {
      console.error("Failed to remove gift card line item:", err);
      setError(
        err instanceof Error ? err.message : "Failed to remove gift card",
      );
    } finally {
      endTaxesLoading();
    }
  };

  // Open the shared ApplyDiscountModal against the sold package transaction:
  // no args = a new discount; "Change" passes the discount group to edit.
  const openPackageDiscountModal = (group?: DiscountGroup) => {
    setPackageDiscountGroup(group ?? null);
    setIsPackageDiscountModalOpen(true);
  };

  // Apply/clear a discount on the package line via the shared edit endpoint,
  // then refetch and re-derive the tax-inclusive balance from the single source
  // of truth (computeTransactionTotals) so the review + checkout reflect it.
  const handlePackageDiscountSubmit = async (
    data: EditTransactionLineItemsRequest,
  ) => {
    if (isApplyingPackageDiscount) return;
    setIsApplyingPackageDiscount(true);
    try {
      await editTransactionLineItems(data);
      if (packageTransaction) {
        const refreshed = await getTransaction(
          packageTransaction.transaction.id,
        );
        if (refreshed) {
          setPackageTransaction(refreshed);
          const totals = computeTransactionTotals(refreshed);
          setPackageRemainingBalance(Number(totals.total.toFixed(2)));
          setPackageTaxTotal(Number(totals.salesTax.toFixed(2)));
        }
      }
    } catch (err) {
      console.error("Failed to update package discount:", err);
      setPackageError(
        err instanceof Error ? err.message : "Failed to apply discount.",
      );
    } finally {
      setIsApplyingPackageDiscount(false);
      setIsPackageDiscountModalOpen(false);
      setPackageDiscountGroup(null);
    }
  };

  // ApplyDiscountModal → PUT the package line's discount set.
  const handlePackageLineDiscounts = async (changes: LineDiscountChange[]) => {
    if (!packageTransaction) return;
    await handlePackageDiscountSubmit(
      buildLineDiscountsRequest(packageTransaction, changes),
    );
  };

  // Phase 2: tax-breakdown-in-cart — create the transaction early so backend
  // tax triggers populate calc__taxes before the operator clicks Checkout.
  // The "first product added" is the trigger; subsequent mutations go
  // through addLineItemToTransaction / deleteLineItem / refetch.
  const ensureTransactionForFirstAdd = useCallback(
    async (product: QuickSaleProductState) => {
      if (createdTransaction) return createdTransaction;
      if (isCreatingTransactionRef.current) return null; // already in flight
      isCreatingTransactionRef.current = true;
      try {
        const request: CreateQuickSaleRequest = {
          clientId: selectedClientId,
          products: [buildQuickSaleProductPayload(product)],
        };
        const response = await createQuickSale(request);
        if (!response?.transactionId) {
          throw new Error("Quick sale creation returned no transaction id");
        }
        const fresh = await getTransaction(response.transactionId);
        if (fresh) {
          setCreatedTransaction(fresh);
        }
        return fresh;
      } catch (err) {
        console.error("Failed to create quick sale on first product add:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to create quick sale transaction",
        );
        return null;
      } finally {
        isCreatingTransactionRef.current = false;
      }
    },
    [createdTransaction, selectedClientId, createQuickSale, getTransaction],
  );

  // Refetch the current createdTransaction so calc__taxes / line_item_taxes
  // land after a backend-side mutation.
  const refetchCreatedTransaction = useCallback(async () => {
    const current = createdTransactionRef.current;
    if (!current) return;
    try {
      const fresh = await getTransaction(current.transaction.id);
      if (fresh) {
        setCreatedTransaction(fresh);
      }
    } catch (err) {
      console.error("Failed to refetch created transaction:", err);
    }
  }, [getTransaction]);

  // Add product to cart. If this is the first product, also create the
  // backend transaction so tax triggers populate calc__taxes immediately.
  // Subsequent adds route through addLineItemToTransaction and refetch.
  const handleAddProduct = async (product: QuickSaleProductState) => {
    // Local cart update first — the UI should feel instant.
    setProducts((prev) => {
      const existingIndex = prev.findIndex(
        (p) => p.productId === product.productId,
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + product.quantity,
        };
        return updated;
      }
      return [...prev, product];
    });

    if (isViewOnly) return;

    // Kick off the backend sync. Flag taxes as in-flight so the summary
    // shows a loading indicator while calc__taxes refreshes.
    beginTaxesLoading();
    try {
      if (!createdTransactionRef.current) {
        // First product — create the quick sale transaction.
        await ensureTransactionForFirstAdd(product);
      } else {
        // Subsequent add — append to the existing transaction then refetch
        // so the summary reflects the latest calc__taxes.
        try {
          await addLineItemToTransaction(
            createdTransactionRef.current.transaction.id,
            {
              // Stacked discounts go through the line-items PUT, not the add.
              products: [
                buildQuickSaleProductPayload(product, {
                  includeDiscounts: false,
                }),
              ],
            },
          );
          await refetchCreatedTransaction();
        } catch (err) {
          console.error(
            "Failed to append product to existing quick sale:",
            err,
          );
          setError(
            err instanceof Error ? err.message : "Failed to add product",
          );
        }
      }
    } finally {
      endTaxesLoading();
    }
  };

  // Update product in cart. Local state mutation for instant UI, then
  // refetch the transaction so calc__taxes stays in sync with the backend.
  // Full backend sync (delete + re-add changed line items) still happens
  // at Checkout time via handleCheckout.
  const handleUpdateProduct = async (
    productId: string,
    updates: Partial<QuickSaleProductState>,
  ) => {
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, ...updates } : p)),
    );
    // Refetch to keep tax display current (line item values are stale until
    // Checkout persists the local edits, but calc__taxes on unchanged items
    // is still accurate from the last backend response).
    if (createdTransactionRef.current) {
      beginTaxesLoading();
      try {
        await refetchCreatedTransaction();
      } finally {
        endTaxesLoading();
      }
    }
  };

  // Remove product from cart. If we already have a backend transaction,
  // delete the corresponding line item and refetch so tax values update.
  const handleRemoveProduct = async (productId: string) => {
    setProducts((prev) => prev.filter((p) => p.productId !== productId));

    const current = createdTransactionRef.current;
    if (!current) return;
    const existing = current.line_items.products.find(
      (p) => p.id === productId,
    );
    if (!existing) return;
    beginTaxesLoading();
    try {
      await deleteLineItem(existing.line_item_id);
      await refetchCreatedTransaction();
    } catch (err) {
      console.error("Failed to remove product from existing quick sale:", err);
      setError(err instanceof Error ? err.message : "Failed to remove product");
    } finally {
      endTaxesLoading();
    }
  };

  // Calculate totals. Gift-card line items live on the real transaction (not the
  // local products mirror) and are tax-exempt, so add their face value straight
  // into the subtotal — the column then foots (Subtotal − Discounts + Tax = Total)
  // and the checkout charges the gift card, which the backend requires paid to mint.
  const calculateSubtotal = useCallback(() => {
    const productsSubtotal = products.reduce((sum, product) => {
      return sum + product.unitPrice * product.quantity;
    }, 0);
    const giftCardSubtotal = (
      createdTransaction?.line_items.gift_cards ?? []
    ).reduce((sum, giftCard) => sum + giftCard.amount, 0);
    return productsSubtotal + giftCardSubtotal;
  }, [products, createdTransaction]);

  // Client-side preview: stacked discounts use the backend math (each line
  // capped at its subtotal); the legacy single discount keeps its own math.
  const calculateDiscounts = useCallback(
    () => calculateCartDiscountTotal(products),
    [products],
  );

  const calculateTotal = useCallback(() => {
    return calculateSubtotal() - calculateDiscounts();
  }, [calculateSubtotal, calculateDiscounts]);

  // Phase 2: sales tax is computed by the backend on line_item insert/update
  // triggers. Before the transaction exists (pre-submit) we display '—'. After
  // the transaction is created we take the tax from the single source of truth
  // (computeTransactionTotals), so the cart and the checkout screen can never
  // disagree — this replaces a hand-rolled calc__taxes sum.
  const calculateSalesTax = useCallback(() => {
    if (!createdTransaction) return 0;
    return computeTransactionTotals(createdTransaction).salesTax;
  }, [createdTransaction]);

  // Calculate total with tax. Pre-submit this equals calculateTotal() because
  // the frontend no longer attempts to estimate tax.
  const calculateTotalWithTax = useCallback(() => {
    return calculateTotal() + calculateSalesTax();
  }, [calculateTotal, calculateSalesTax]);

  // Calculate paid amount
  const calculatePaidAmount = useCallback(() => {
    return (
      createdTransaction?.payments
        ?.filter((payment) => payment.status === "succeeded")
        ?.reduce((sum, payment) => sum + payment.amount, 0) || 0
    );
  }, [createdTransaction]);

  // Calculate remaining balance (with tax)
  const calculateRemainingBalance = useCallback(() => {
    return calculateTotalWithTax() - calculatePaidAmount();
  }, [calculateTotalWithTax, calculatePaidAmount]);

  // Bring the persisted quick sale in line with the cart before
  // checkout — drop removed lines, add new ones, then one line-items PUT for
  // quantity / commission target / stacked discount changes (incl. the lines
  // just added, once their ids are known).
  const syncCartToTransaction = async (
    transaction: GetTransactionsResponse,
  ) => {
    const transactionId = transaction.transaction.id;
    const cartIds = new Set(products.map((p) => p.productId));
    for (const line of transaction.line_items.products) {
      if (!cartIds.has(line.id)) await deleteLineItem(line.line_item_id);
    }

    const persistedIds = new Set(
      transaction.line_items.products.map((line) => line.id),
    );
    const newProducts = products.filter((p) => !persistedIds.has(p.productId));
    let current = transaction;
    if (newProducts.length > 0) {
      await addLineItemToTransaction(transactionId, {
        products: newProducts.map((p) =>
          buildQuickSaleProductPayload(p, { includeDiscounts: false }),
        ),
      });
      current = (await getTransaction(transactionId)) ?? transaction;
    }

    const edits = buildCartProductEdits(products, current);
    if (edits.length > 0) {
      await editTransactionLineItems({
        transaction_id: transactionId,
        line_items: { products: edits },
      });
    }
  };

  // Handle checkout - creates or updates transaction and opens checkout modal
  const handleCheckout = async () => {
    // A gift-card line item lives on the real transaction and is billable on
    // its own, so a cart holding only a gift card (e.g. after the product that
    // created the sale was removed) must still reach checkout — otherwise the
    // card can never be paid for and never mints. Block only a truly empty
    // cart: no products AND no gift-card line items.
    const giftCardLineItems = createdTransaction?.line_items.gift_cards ?? [];
    if (products.length === 0 && giftCardLineItems.length === 0) {
      setError("Please add at least one product");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let transactionId: string;

      // Check if we already have a transaction (re-checkout scenario)
      if (createdTransaction) {
        await syncCartToTransaction(createdTransaction);
        transactionId = createdTransaction.transaction.id;
      } else {
        // Create new transaction
        const request: CreateQuickSaleRequest = {
          clientId: selectedClientId,
          products: products.map((p) => buildQuickSaleProductPayload(p)),
        };

        const response = await createQuickSale(request);

        if (!response || !response.transactionId) {
          setError("Failed to create transaction");
          return;
        }

        transactionId = response.transactionId;
      }

      // Fetch the full transaction details
      const transaction = await getTransaction(transactionId);

      if (transaction) {
        setCreatedTransaction(transaction);
        // Staged cart discounts are now applied: pick up their ids so a
        // later re-checkout keeps them instead of re-applying the preset.
        setProducts((prev) =>
          syncCartDiscountsFromTransaction(prev, transaction),
        );
        setShowCheckoutModal(true);
      } else {
        setError("Transaction created but could not retrieve details");
      }
    } catch (err) {
      console.error("Error processing quick sale:", err);
      setError(
        err instanceof Error ? err.message : "Failed to process quick sale",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle checkout modal close - determines if payment was completed or cancelled
  const handleCheckoutModalClose = async () => {
    setShowCheckoutModal(false);

    // Use ref to get the LATEST transaction value (avoids stale closure)
    const latestTransaction = createdTransactionRef.current;

    if (!latestTransaction) return;

    try {
      // Refetch the transaction to get the latest payment information
      const refreshedTransaction = await getTransaction(
        latestTransaction.transaction.id,
      );

      if (refreshedTransaction) {
        // Update the transaction state with fresh data
        setCreatedTransaction(refreshedTransaction);

        // Calculate the remaining balance with the refreshed data
        const paidAmount =
          refreshedTransaction.payments
            ?.filter((payment: Payment) => payment.status === "succeeded")
            ?.reduce(
              (sum: number, payment: Payment) => sum + payment.amount,
              0,
            ) || 0;

        // Compute total entirely from the refreshed transaction to avoid
        // mixing local products state with backend-computed values.
        const lineItemTotal =
          (refreshedTransaction.line_items.products || []).reduce(
            (sum: number, p: { calc__cost?: number }) =>
              sum + (p.calc__cost || 0),
            0,
          ) +
          (refreshedTransaction.line_items.booked_services || []).reduce(
            (sum: number, s: { calc__cost?: number }) =>
              sum + (s.calc__cost || 0),
            0,
          );
        const currentBalance = lineItemTotal - paidAmount;

        // Check if payment was completed successfully
        const hasSuccessfulPayment = refreshedTransaction.payments?.some(
          (p: Payment) => p.status === "succeeded",
        );

        if (hasSuccessfulPayment && currentBalance <= 0.01) {
          // Payment complete - enter view-only mode and trigger completion callback
          setIsViewOnly(true);
          // If the paid sale included a gift-card line item, the card just minted —
          // advance to the post-checkout fork (attach to a client / text the claim
          // link), reusing the same modal as the standalone sale.
          const hadGiftCards =
            (refreshedTransaction.line_items.gift_cards ?? []).length > 0;
          if (hadGiftCards) {
            setGiftCardTransactionUuid(refreshedTransaction.transaction.id);
            setShowGiftCardPostCheckout(true);
          }
          if (onQuickSaleComplete) {
            onQuickSaleComplete(refreshedTransaction.transaction.id);
          }
        }
      }
    } catch (err) {
      console.error("Error refetching transaction after checkout:", err);
      // Don't show error to user, just log it
    }
    // Otherwise, keep transaction and stay in products view so user can make changes
  };

  // Handle transaction updated from checkout
  const handleTransactionUpdated = (
    updatedTransaction: GetTransactionsResponse | TransactionResponse,
  ) => {
    setCreatedTransaction(updatedTransaction as GetTransactionsResponse);
  };

  // Get selected client info
  const selectedClient = allClients.find(
    (c: { id: string }) => c.id === selectedClientId,
  );

  // Calculate all values for display and checkout
  const subtotal = calculateSubtotal();
  const discounts = calculateDiscounts();
  const total = calculateTotal();
  const salesTax = calculateSalesTax();
  const totalWithTax = calculateTotalWithTax();
  const paidAmount = calculatePaidAmount();
  const remainingBalance = calculateRemainingBalance();

  // Render client selection step
  const renderClientStep = () => (
    <Stack gap="lg">
      <ClientLookup
        value={selectedClientId}
        onChange={handleClientSelect}
        error={clientError}
      />

      <Button
        onClick={handleProceedFromClient}
        disabled={!selectedClientId}
        size="lg"
        color="var(--color-primary)"
        fullWidth
      >
        Continue
      </Button>
    </Stack>
  );

  // Sale-type chooser: products cart vs the package/special sell sub-flow.
  const renderSaleTypeStep = () => (
    <Stack gap="lg">
      <Card
        withBorder
        padding="lg"
        radius="md"
        component="button"
        type="button"
        style={{ cursor: "pointer" }}
        onClick={() => setStep("products")}
        aria-label="Sell products"
      >
        <Group wrap="nowrap" align="flex-start" gap="md">
          <IconShoppingBag size={28} />
          <Stack gap={2} align="flex-start">
            <Text fw={600}>Sell products</Text>
            <Text size="sm" c="dimmed" ta="left">
              Ring up retail products for this client.
            </Text>
          </Stack>
        </Group>
      </Card>

      <Card
        withBorder
        padding="lg"
        radius="md"
        component="button"
        type="button"
        style={{ cursor: "pointer" }}
        onClick={handleOpenPackageSell}
        aria-label="Sell a package or special"
      >
        <Group wrap="nowrap" align="flex-start" gap="md">
          <IconPackage size={28} />
          <Stack gap={2} align="flex-start">
            <Text fw={600}>Sell a package or special</Text>
            <Text size="sm" c="dimmed" ta="left">
              Prepaid bundles and limited-time specials, paid upfront.
            </Text>
          </Stack>
        </Group>
      </Card>

      {giftCardsEnabled && canSellGiftCards && (
        <Card
          withBorder
          padding="lg"
          radius="md"
          component="button"
          type="button"
          style={{ cursor: "pointer" }}
          onClick={handleOpenGiftCardSell}
          aria-label="Sell a gift card"
        >
          <Group wrap="nowrap" align="flex-start" gap="md">
            <IconGiftCard size={28} />
            <Stack gap={2} align="flex-start">
              <Text fw={600}>Sell a gift card</Text>
              <Text size="sm" c="dimmed" ta="left">
                Sell virtual gift cards that can be tied to a client profile and
                redeemed at checkout
              </Text>
            </Stack>
          </Group>
        </Card>
      )}

      <Button
        variant="subtle"
        color="gray"
        onClick={() => setStep("client")}
        fullWidth
      >
        Back
      </Button>
    </Stack>
  );

  // Package-sale picker (select → sellServicePackage → checkout screen). The
  // grouped Select + detail card live in the shared PackageSpecialPicker so
  // Quick Sale and the appointment modal stay in lockstep.
  const renderPackageSellStep = () => (
    <PackageSpecialPicker
      packages={packages}
      isLoading={isLoadingPackages}
      selectedPackageId={selectedPackageId}
      onSelectPackage={setSelectedPackageId}
      onSubmit={handleSellPackage}
      submitLabel="Review Sale"
      isSubmitting={isSellingPackage}
      error={packageError}
      onClearError={() => setPackageError(null)}
      timezone={businessInfo?.timezone}
    />
  );

  // Review the sold package before payment, with an optional discount applied
  // through the shared grouped-discount flow (consistent with the appointment
  // transaction). Continue proceeds to the 100%-upfront checkout screen.
  const renderPackageReviewStep = () => {
    const pkgLine = packageTransaction?.line_items.service_packages?.[0];
    const groupedDiscounts = packageTransaction
      ? calculateGroupedDiscounts(packageTransaction)
      : [];

    return (
      <Stack gap="lg">
        {packageError && (
          <Alert
            color="red"
            withCloseButton
            onClose={() => setPackageError(null)}
          >
            {packageError}
          </Alert>
        )}

        <Card withBorder radius="md">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <IconPackage size={18} />
              <Text fw={600}>{pkgLine?.name ?? "Package"}</Text>
            </Group>
            <Text>{formatCurrency(pkgLine?.unit_price ?? 0)}</Text>
          </Group>
        </Card>

        {groupedDiscounts.length > 0 ? (
          groupedDiscounts.map((discount) => (
            <Group key={discount.id} justify="space-between" wrap="nowrap">
              <Text size="sm">
                {discount.name}{" "}
                {formatDiscountValue(discount.discount_type, discount.amount)}
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Button
                  variant="subtle"
                  size="xs"
                  disabled={isApplyingPackageDiscount}
                  onClick={() =>
                    packageTransaction && openPackageDiscountModal(discount)
                  }
                >
                  Change
                </Button>
                <Button
                  variant="subtle"
                  size="xs"
                  color="red"
                  loading={isApplyingPackageDiscount}
                  onClick={() =>
                    packageTransaction &&
                    handlePackageDiscountSubmit(
                      buildRemoveDiscountGroupRequest(
                        packageTransaction,
                        discount,
                      ),
                    )
                  }
                >
                  Remove
                </Button>
              </Group>
            </Group>
          ))
        ) : (
          <Button
            variant="light"
            color="var(--brand-teal)"
            disabled={isApplyingPackageDiscount}
            onClick={() => openPackageDiscountModal()}
          >
            Add discount
          </Button>
        )}
        {/* Discounts stack, so another can always be added. */}
        {groupedDiscounts.length > 0 && (
          <Button
            variant="light"
            color="var(--brand-teal)"
            disabled={isApplyingPackageDiscount}
            onClick={() => openPackageDiscountModal()}
          >
            Add another discount
          </Button>
        )}

        <Divider />

        <Group justify="space-between">
          <Text fw={600}>Total</Text>
          <Text fw={700}>{formatCurrency(packageRemainingBalance)}</Text>
        </Group>
        {packageTaxTotal > 0 && (
          <Text size="xs" c="dimmed" ta="right" mt={-8}>
            Includes {formatCurrency(packageTaxTotal)} sales tax
          </Text>
        )}

        {/* No "Back" here: the package sale is already committed server-side,
            so returning to the picker would orphan it / risk a duplicate sale.
            Closing the modal abandons the Quick Sale (same as checkout). */}
        <Group justify="flex-end" mt="sm">
          <Button
            color="var(--brand-teal)"
            onClick={() => {
              setShowPackageReview(false);
              setShowPackageCheckout(true);
            }}
          >
            Continue to Payment
          </Button>
        </Group>
      </Stack>
    );
  };

  // Render products step
  const renderProductsStep = () => (
    <Grid
      gutter={0}
      h="100%"
      className={
        isMobile ? sharedStyles.mobileLayout : sharedStyles.gridContainer
      }
    >
      {/* Left Side - Product Details */}
      <Grid.Col
        span={isMobile ? 12 : 8}
        className={
          isMobile
            ? sharedStyles.mobileDetailsSection
            : sharedStyles.gridDetailsCol
        }
      >
        <ScrollArea p={0} h={isMobile || isTablet ? "auto" : "100dvh"}>
          <QuickSaleDetails
            products={products}
            client={selectedClient}
            onAddProduct={handleAddProduct}
            onUpdateProduct={handleUpdateProduct}
            onRemoveProduct={handleRemoveProduct}
            isViewOnly={isViewOnly}
            transactionDate={
              createdTransaction
                ? (createdTransaction.transaction as any).created_dt
                : undefined
            }
            timezone={businessInfo?.timezone}
            giftCardsEnabled={giftCardsEnabled}
            canSellGiftCards={canSellGiftCards}
            giftCardLineItems={createdTransaction?.line_items.gift_cards ?? []}
            canAddGiftCard={!!createdTransaction}
            onAddGiftCard={() => setShowGiftCardLineItemModal(true)}
            onRemoveGiftCard={handleRemoveGiftCardLineItem}
          />
        </ScrollArea>
      </Grid.Col>

      {/* Right Side - Summary */}
      <Grid.Col
        span={isMobile ? 12 : 4}
        p={isMobile ? "sm" : isTablet ? "md" : "md"}
        className={
          isMobile
            ? sharedStyles.mobileSummarySection
            : sharedStyles.gridSummaryCol
        }
        style={{ backgroundColor: "#f8f9fa" }}
        h="fill"
      >
        <QuickSaleSummary
          transaction={createdTransaction as GetTransactionsResponse}
          products={products}
          subtotal={subtotal}
          discounts={discounts}
          total={total}
          salesTax={salesTax}
          totalWithTax={totalWithTax}
          paidAmount={paidAmount}
          remainingBalance={remainingBalance}
          onCheckout={handleCheckout}
          onClose={handleClose}
          isLoading={isLoading}
          isTaxesLoading={isTaxesLoading}
          error={error}
          businessInfo={businessInfo}
          isViewOnly={isViewOnly}
        />
      </Grid.Col>
    </Grid>
  );

  return (
    <>
      {/* Client Selection + Sale Type Modal - Small */}
      <Modal
        opened={
          opened &&
          (step === "client" || step === "saleType") &&
          !showCheckoutModal &&
          !showPackageSell &&
          !showPackageReview &&
          !showPackageCheckout
        }
        onClose={handleClose}
        title="Quick Sale"
        size="md"
        centered
      >
        {step === "client" ? renderClientStep() : renderSaleTypeStep()}
      </Modal>

      {/* Package Sale Picker Modal */}
      <Modal
        opened={opened && showPackageSell && !showPackageCheckout}
        onClose={() => setShowPackageSell(false)}
        title="Sell a Package or Special"
        size="md"
        centered
        fullScreen={isMobile}
      >
        {renderPackageSellStep()}
      </Modal>

      {/* Package Sale Review (optional discount before checkout) */}
      <Modal
        opened={opened && showPackageReview && !showPackageCheckout}
        onClose={handleClose}
        title="Review Package Sale"
        size="md"
        centered
        fullScreen={isMobile}
      >
        {renderPackageReviewStep()}
      </Modal>

      {/* Shared discount modal for the package sale (mirrors the appointment
          transaction's grouped-discount flow). */}
      {packageTransaction && (
        <ApplyDiscountModal
          opened={isPackageDiscountModalOpen}
          onClose={() => {
            setIsPackageDiscountModalOpen(false);
            setPackageDiscountGroup(null);
          }}
          lines={buildDiscountableLines(packageTransaction)}
          editingGroup={packageDiscountGroup}
          onApply={handlePackageLineDiscounts}
        />
      )}

      {/* Products Modal - Full Screen */}
      <Modal
        opened={opened && step === "products"}
        onClose={handleClose}
        fullScreen
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        {error && (
          <Alert color="red" mb="md" mx="md" mt="md">
            {error}
          </Alert>
        )}

        {renderProductsStep()}
      </Modal>

      {/* Checkout (redesigned fullscreen screen) */}
      {showCheckoutModal && createdTransaction && businessInfo && (
        <CheckoutScreenModal
          opened={showCheckoutModal}
          onClose={handleCheckoutModalClose}
          transaction={createdTransaction}
          remainingBalance={Number(remainingBalance.toFixed(2))}
          businessInfo={businessInfo}
          onTransactionUpdated={handleTransactionUpdated}
          onRefetchTransaction={async () => {
            if (createdTransaction) {
              const refreshed = await getTransaction(
                createdTransaction.transaction.id,
              );
              if (refreshed) {
                setCreatedTransaction(refreshed);
              }
            }
          }}
        />
      )}

      {/* Package Sale Checkout (100% upfront) */}
      {showPackageCheckout && packageTransaction && businessInfo && (
        <CheckoutScreenModal
          opened={showPackageCheckout}
          onClose={handleClose}
          transaction={packageTransaction}
          remainingBalance={packageRemainingBalance}
          amountDue={packageRemainingBalance}
          amountNote={
            packageTaxTotal > 0
              ? `Includes ${formatCurrency(packageTaxTotal)} sales tax`
              : undefined
          }
          packageSaleName={
            packages.find((pkg) => pkg.id === selectedPackageId)?.name
          }
          businessInfo={businessInfo}
          onTransactionUpdated={handlePackageTransactionUpdated}
          onRefetchTransaction={async () => {
            if (!packageTransaction) return;
            const refreshed = await getTransaction(
              packageTransaction.transaction.id,
            );
            if (refreshed) {
              handlePackageTransactionUpdated(refreshed);
            }
          }}
        />
      )}

      {/* Gift Card Sale (dedicated modal → own unpaid sale transaction) */}
      {giftCardsEnabled && businessInfo && (
        <GiftCardSaleModal
          opened={opened && showGiftCardSell && !showGiftCardCheckout}
          onClose={() => setShowGiftCardSell(false)}
          salonId={businessInfo.id}
          purchaserClientId={selectedClientId || null}
          purchaserName={
            selectedClient
              ? `${selectedClient.first_name} ${selectedClient.last_name}`.trim()
              : null
          }
          onSold={handleGiftCardSold}
        />
      )}

      {/* Gift Card Sale Checkout (100% upfront; card minted on payment) */}
      {showGiftCardCheckout && giftCardTransaction && businessInfo && (
        <CheckoutScreenModal
          opened={showGiftCardCheckout}
          onClose={handleGiftCardCheckoutClose}
          transaction={giftCardTransaction}
          remainingBalance={giftCardRemainingBalance}
          amountDue={giftCardRemainingBalance}
          amountNote={
            giftCardTaxTotal > 0
              ? `Includes ${formatCurrency(giftCardTaxTotal)} sales tax`
              : undefined
          }
          packageSaleName="Gift Card"
          businessInfo={businessInfo}
          onTransactionUpdated={handleGiftCardTransactionUpdated}
          onRefetchTransaction={async () => {
            if (!giftCardTransaction) return;
            const refreshed = await getTransaction(
              giftCardTransaction.transaction.id,
            );
            if (refreshed) {
              handleGiftCardTransactionUpdated(refreshed);
            }
          }}
        />
      )}

      {/* Post-checkout fork: attach the minted card to a client, or text the
          recipient a claim link (opens after the paid checkout is dismissed). */}
      {giftCardsEnabled && businessInfo && (
        <GiftCardPostCheckoutModal
          opened={showGiftCardPostCheckout}
          onClose={handleGiftCardPostCheckoutDone}
          salonId={businessInfo.id}
          transactionUuid={giftCardTransactionUuid}
          purchaserName={
            selectedClient
              ? `${selectedClient.first_name} ${selectedClient.last_name}`.trim()
              : null
          }
        />
      )}

      {/* Add a gift card as a line item on the current multi-item quick sale */}
      {giftCardsEnabled && businessInfo && (
        <Modal
          opened={showGiftCardLineItemModal}
          onClose={() => setShowGiftCardLineItemModal(false)}
          title="Sell a Gift Card"
          size="md"
          centered
        >
          <GiftCardLineItemForm
            salonId={businessInfo.id}
            onSubmit={handleAddGiftCardLineItem}
            onCancel={() => setShowGiftCardLineItemModal(false)}
          />
        </Modal>
      )}
    </>
  );
}
